// src/components/routes/Forms/Response/Responses.js

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format } from 'date-fns';
import { Link, useParams } from "react-router-dom";
import "./Responses.css";

const Responses = () => {
  const { instanceId } = useParams();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(1);
  const [startDate, setStartDate] = useState(
    format(new Date(new Date().setDate(new Date().getDate() - 30)), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // New state for conversation modal
  const [showConversation, setShowConversation] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [showGroupMessages, setShowGroupMessages] = useState(false);
  const [replyToGroup, setReplyToGroup] = useState(false);

  // Helper function to detect if a number is a group ID (either has @g.us or matches group format)
  const isGroupJid = (number) => {
    if (!number || typeof number !== 'string') return false;
    return (
      number.endsWith('@g.us') || 
      number.includes('-') || // Group IDs often contain hyphens
      /^12/.test(number)     // Group IDs often start with 12
    );
  };

  // Helper function to format group ID
  const formatGroupId = (number) => {
    // If it already has @g.us suffix, return as is
    if (number.endsWith('@g.us')) return number;
    
    // If it's a group ID format (has hyphen or starts with 120)
    if (isGroupJid(number)) {
      return `${number}@g.us`;
    }
    
    // Otherwise treat as regular number
    const cleanedNumber = number.replace(/\s+/g, '').replace(/^[+]/, '');
    return cleanedNumber.length === 10 ? '91' + cleanedNumber : cleanedNumber;
  };

  useEffect(() => {
    if (instanceId) {
      checkVerification();
      checkConnection();
      fetchResponses();
    }
  }, [instanceId, startDate, endDate, currentPage]);

  useEffect(() => {
    if (selectedNumber) {
      fetchConversation();
    }
  }, [selectedNumber]);

  useEffect(() => {
    if (instanceId) {
      fetchResponses();
    }
  }, [showGroupMessages]);

  const checkVerification = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const apiUrl = process.env.REACT_APP_API_URL;
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      const userEmail = decodedToken.email;

      if (!userEmail) {
        setIsVerified(false);
        return;
      }

      const response = await axios.get(`${apiUrl}/verify/user/${userEmail}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setIsVerified(response.data.verified === 'yes' || response.data.verified === true);
    } catch (error) {
      console.error("Failed to check verification status:", error);
      setIsVerified(false);
    }
  };

  const checkConnection = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("token");
      
      if (!token || !instanceId) {
        setIsConnected(false);
        return;
      }

      const response = await axios.get(`${apiUrl}/${instanceId}/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        if (response.data.status === 'disconnected') {
          setIsConnected(false);
          console.log("WhatsApp is disconnected");
        } else if (response.data.status === 'reconnecting') {
          setIsConnected(false);
          console.log("WhatsApp is reconnecting...");
        } else {
          setIsConnected(true);
          console.log("WhatsApp connected successfully!");
        }
      } else {
        setIsConnected(false);
      }
    } catch (error) {
      console.error("Failed to check connection status:", error);
      setIsConnected(false);
      
      if (error.response?.status === 404) {
        console.error("Instance not found or invalid instance ID");
      }
    }
  };

  const formatText = (text, formatType) => {
    // Remove existing formatting
    text = text.replace(/[*_]/g, '');
    
    if (formatType === 'bold') {
      return `*${text}*`;
    } else if (formatType === 'italic') {
      return `_${text}_`;
    }
    return text;
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      console.log("Reply text is empty");
      return;
    }

    try {
      setSendingReply(true);
      setInputDisabled(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("token");
      
      // Use group endpoint if the number is a group JID
      const isGroup = isGroupJid(selectedNumber);
      let endpoint = isGroup 
        ? `${apiUrl}/${instanceId}/send-group-message`
        : `${apiUrl}/${instanceId}/send-media-one`;

      const hasValidSubscription = await checkSubscription(instanceId);
      if (!hasValidSubscription) {
        throw new Error("No active subscription found. Please subscribe first.");
      }

      // Format the number based on whether it's a group or individual chat
      const formattedNumber = formatGroupId(selectedNumber);

      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          const response = await axios.post(
            endpoint,
            {
              messages: [{
                number: formattedNumber,
                text: replyMessage
              }]
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              }
            }
          );

          if (response.data.success) {
            setReplyMessage('');
            setShouldAutoScroll(true);
            scrollToBottom(true);
            await fetchConversation();
            break;
          } else {
            throw new Error(response.data.message || "Failed to send message");
          }
        } catch (error) {
          retryCount++;
          if (retryCount === maxRetries) {
            throw error;
          }
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        }
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to send message";
      alert(errorMessage);
    } finally {
      setSendingReply(false);
      setInputDisabled(false);
    }
  };

  const handleRowClick = (recipientNumber) => {
    const isGroup = isGroupJid(recipientNumber);
    setSelectedNumber(recipientNumber);
    setReplyToGroup(isGroup);
    setShowConversation(true);
  };

  const fetchConversation = async () => {
    try {
      if (!selectedNumber) return;
      
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("token");
      
      // Format the number based on whether it's a group or individual chat
      const formattedNumber = formatGroupId(selectedNumber);

      const response = await axios.get(
        `${apiUrl}/${instanceId}/conversations/${formattedNumber}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );

      if (response.data.success) {
        // Sort messages by timestamp in ascending order (oldest first)
        const sortedMessages = response.data.messages.sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        );
        setConversation(sortedMessages);
        // Scroll to bottom after messages are loaded
        setTimeout(() => {
          scrollToBottom(true);
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
      alert('Failed to fetch conversation history');
    }
  };

  useEffect(() => {
    if (selectedNumber) {
      // Initial fetch
      fetchConversation();
      
      // Set up polling every 3 seconds
      const interval = setInterval(fetchConversation, 3000);
      setPollingInterval(interval);
      
      // Cleanup polling when component unmounts or number changes
      return () => {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      };
    }
  }, [selectedNumber]);

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    };
  }, []);

  const checkSubscription = async (instanceId) => {
    try {
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL;
      
      const response = await axios.get(`${apiUrl}/${instanceId}/check-subscription`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data.success && response.data.hasSubscription;
    } catch (error) {
      console.error("Error checking subscription:", error);
      return false;
    }
  };

  const closeConversation = () => {
    setShowConversation(false);
    setSelectedNumber(null);
    setConversation([]);
    setReplyMessage('');
    window.location.reload();
  };

  const fetchResponses = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("token");
      
      const limit = 15;
      const offset = (currentPage - 1) * limit;
      
      const response = await axios.get(
        `${apiUrl}/${instanceId}/whatsapp-responses`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            start_date: startDate ? `${startDate} 00:00:00` : undefined,
            end_date: endDate ? `${endDate} 23:59:59` : undefined,
            limit,
            offset,
            groupsOnly: showGroupMessages, // When true, show group messages
            excludeGroups: !showGroupMessages // When true (toggle is off), exclude group messages
          }
        }
      );

      if (response.data.success) {
        const messages = response.data.responses;
        // Additional client-side filtering
        const filteredMessages = showGroupMessages
          ? messages.filter(msg => isGroupJid(msg.recipient_number))
          : messages.filter(msg => !isGroupJid(msg.recipient_number));
        
        setResponses(filteredMessages);
        setTotalRows(showGroupMessages 
          ? response.data.pagination.groupTotal || filteredMessages.length 
          : response.data.pagination.singleTotal || filteredMessages.length
        );
      }
    } catch (error) {
      console.error('Error fetching responses:', error);
      setError('Failed to fetch responses');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const formatDateTime = (dateTimeStr) => {
    return format(new Date(dateTimeStr), 'dd/MM/yyyy HH:mm:ss');
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Add ref for messages container
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const lastMessageCountRef = useRef(0);

  // Function to check if we're near bottom
  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return false;
    
    const threshold = 100; // pixels from bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };

  // Function to scroll to bottom
  const scrollToBottom = (force = false) => {
    if (force || shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: force ? "auto" : "smooth" });
    }
  };

  // Handle scroll events
  const handleScroll = () => {
    setShouldAutoScroll(isNearBottom());
  };

  // Only scroll on new messages
  useEffect(() => {
    if (!conversation) return;
    
    // Check if we have new messages
    const hasNewMessages = conversation.length > lastMessageCountRef.current;
    lastMessageCountRef.current = conversation.length;
    
    // Only scroll if there are new messages
    if (hasNewMessages) {
      scrollToBottom();
    }
  }, [conversation]);

  // Initial scroll when conversation opens
  useEffect(() => {
    if (selectedNumber) {
      scrollToBottom(true); // force scroll on conversation change
      setShouldAutoScroll(true);
    }
  }, [selectedNumber]);

  if (loading) return <div className="loading">Loading responses...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="responses-container">
      <div className="headerr-container">
        <Link to={`/${instanceId}/reports`}>
          <img src="/uploads/house-fill.svg" alt="Home" className="homeee-icon" />
        </Link>
        <h1 className="responses-title">WhatsApp Responses</h1>
      </div>

      <div className="filters-section">
        <div className="filters-content">
          <div className="date-filters">
            <div className="filter-item">
              <label>Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="filter-item">
              <label>End Date:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="toggle-filter">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={showGroupMessages}
                onChange={(e) => {
                  setShowGroupMessages(e.target.checked);
                  fetchResponses();
                }}
              />
              <span className="slider round"></span>
            </label>
            <span className="toggle-label">Show Group Messages</span>
          </div>
        </div>
      </div>

      <style>
        {`
          .filters-section {
            margin: 5px 0;
            padding: 5px;
            background: #1e1e1e;
            border-radius: 8px;
          }

          .filters-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 24px;
          }

          .date-filters {
            display: flex;
            align-items: center;
            gap: 20px;
          }

          .filter-item {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .filter-item label {
            font-size: 14px;
            color: white;
            white-space: nowrap;
          }

          .filter-item input[type="date"] {
            padding: 1px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            color: #333;
          }

          .toggle-filter {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-left: auto;
          }

          .toggle-switch {
            position: relative;
            display: inline-block;
            width: 46px;
            height: 24px;
            margin: 0;
          }

          .toggle-switch input {
            opacity: 0;
            width: 0;
            height: 0;
            margin: 0;
          }

          .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #ccc;
            transition: .3s;
            border-radius: 24px;
          }

          .slider:before {
            position: absolute;
            content: "";
            height: 18px;
            width: 18px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: .3s;
            border-radius: 50%;
          }

          input:checked + .slider {
            background-color: #2196F3;
          }

          input:checked + .slider:before {
            transform: translateX(22px);
          }

          .toggle-label {
            font-size: 14px;
            color: white;
            white-space: nowrap;
            user-select: none;
          }
        `}
      </style>

      <div className="table-container">
        <table className="reports-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Recipient Number</th>
              <th>Response</th>
              <th>Sender Name</th>
              <th>Received At</th>
            </tr>
          </thead>
          <tbody>
            {responses.length > 0 ? (
              responses.map((response, index) => (
                <tr 
                  key={response.id}
                  onClick={() => handleRowClick(response.recipient_number)}
                  className="clickable-row"
                >
                  <td>{(currentPage - 1) * 10 + index + 1}</td>
                  <td>{response.recipient_number}</td>
                  <td>{response.response}</td>
                  <td>{response.sender_name || 'N/A'}</td>
                  <td>{formatDateTime(response.received_at)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">No responses found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {responses.length > 0 && (
        <div className="paginationn">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-button"
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {Math.ceil(totalRows / 15)}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === Math.ceil(totalRows / 15)}
            className="page-button"
          >
            Next
          </button>
        </div>
      )}

      {showConversation && (
        <div className="modal-overlay">
          <div className="conversation-modal">
            <div className="modal-header">
              <h3>Conversation with {selectedNumber}</h3>
              <div className="conversation-controls">
                <button onClick={closeConversation} className="close-button">×</button>
              </div>
            </div>
            <div 
              className="conversation-messages" 
              ref={messagesContainerRef}
              onScroll={handleScroll}
            >
              {conversation.map((msg, index) => (
                <div 
                  key={index} 
                  className={`message ${msg.type === 'received' ? 'received' : 'sent'}`}
                >
                  <div className="message-content">{msg.content}</div>
                  <div className="message-time">
                    {formatMessageTime(msg.timestamp)}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div className="reply-section">
              <div className="formatting-buttons">
                <button 
                  type="button" 
                  onClick={() => setReplyMessage(formatText(replyMessage, 'bold'))}
                  className="format-btn"
                  title="Bold"
                  disabled={!isVerified}
                >
                  <strong>B</strong>
                </button>
                <button 
                  type="button" 
                  onClick={() => setReplyMessage(formatText(replyMessage, 'italic'))}
                  className="format-btn"
                  title="Italic"
                  disabled={!isVerified}
                >
                  <em>I</em>
                </button>
              </div>
              <input
                type="text"
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your message..."
                className="reply-input"
                onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                disabled={!isVerified || !isConnected || inputDisabled}
              />
              <button 
                onClick={handleSendReply}
                disabled={sendingReply || !replyMessage.trim() || !isVerified || !isConnected || inputDisabled}
                className="send-button"
              >
                {sendingReply ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Responses;